/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useReducer, useRef, useCallback } from 'react';
import { Grid, Checkbox, Label, Input, Box, Flex, Button, Image, Text } from 'theme-ui';
/** @jsx jsx */ /** @jsxRuntime classic */ import { jsx } from "theme-ui";
import styled from 'styled-components';
import { StyledButton } from "./styles.js";
import concentrationsList from './concentrations.js';
import CreatableMulti from './CreatableMulti.js';
import clubs from './clubs.js';
import ProfileInput from './ProfileInput';
import { useFirebaseProfile } from './useFirebaseProfile';
import { Camera } from 'lucide-react';  // Import the Camera icon from lucide-react
import Toast from './Toast';
import defaultPicture from './images/default.png';
import { getAuth, linkWithPopup, GoogleAuthProvider } from "firebase/auth";
import { TbRuler } from 'react-icons/tb';

const auth = getAuth();

// styling for input multi select fields
const customStyles = {
    control: (provided) => ({
      ...provided,
      border: 'none',
      boxShadow: 'none',
      background: 'transparent',
      padding: '5px 0',
    }),
    multiValue: (provided, state) => ({
        ...provided,
        // backgroundColor: 'rgba(254, 158, 162, 1)',
        backgroundColor: getRandomColor(state.data.value),
        borderRadius: '20px',
        padding: '2px 8px',
        margin: '2px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'white',
      fontSize: '14px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'white',
      ':hover': {
        backgroundColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.8)',
      },
    }),
    placeholder: (provided) => ({...provided, color: '#888'}),
    input: (provided) => ({...provided, color: '#333'}),
};

const getRandomColor = (seed) => {
    const hue = Math.abs(seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % 360;
    return `hsl(${hue}, 70%, 80%)`;
};

const InputContainer = styled.div`
  background-color: #fcfcfcfc;
  border-radius: 20px;
  padding: 10px 15px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border: 2px solid #CCC7C7;
  margin-top: 6px;
`;

const StyledInput = styled.input`
  background-color: transparent;
  border: none;
  width: 100%;
  font-family: 'Mulish', sans-serif;
  font-size: 16px;
  margin-left: 10px;

  &:focus {
    outline: none;
  }
`;

const StyledTextarea = styled.textarea`
  background-color: transparent;
  border: none;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  padding: 10px;
  resize: vertical;
  min-height: 100px;
  &:focus {
    outline: none;
  }
`;

const topicsOptions = [
    { value: "Research", label: "Research" }, 
    { value: "Grad School", label: "Grad School" }, 
    { value: "Finding your interests/career path", label: "Finding your interests/career path" }, 
    { value: "Product Management", label: "Product Management" }, 
    { value: "SWE", label: "SWE" }
];

const interestOptions = [];

// Getting list of all clubs 
const clubsValues = clubs;
const clubsList = clubsValues.split(/\r\n|\n/);

const clubOptions = [];

for (var i = 0; i < 613; i++) {
    clubOptions[i] = {
        value: clubsList[i],
        label: clubsList[i]
    }
}

const userInfo = {
    grade: {
        label: "Class Year",
        type: "number",
        display: "header",
        value: "",
    },
    pronouns: {
        label: "Pronouns",
        type: "string",
        display: "header",
        value: ""
    },
    hometown: {
        label: "Hometown",
        type: "string",
        display: "header",
        value: "",
    },
    concentration: {
        label: "Concentration",
        type: "multiselect",
        display: "header",
        value: "",
    },
    careerPath: {
        label: "Current / Most Recent Job",
        type: "string",
        display: "header",
        value: "",
    },
    fgli: {
        label: "FGLI",
        type: "boolean",
        display: "header",
        value: false,
    },
    international: {
        label: "International",
        type: "boolean",
        display: "header",
        value: false,
    },
    bio: {
        label: "Bio",
        type: "long string",
        display: "detailed",
        value: "",
    },
    // courses: {
    //     label: "SEAS Courses Taken",
    //     type: "multiselect",
    //     display: "detailed",
    //     value: "",
    //     options: { courseOptions }
    // },
    extracurriculars: {
        label: "Extracurriculars",
        type: "multiselect",
        display: "detailed",
        value: "",
        options: { clubOptions }
    },
    companies: {
        label: "Companies",
        type: "long string",
        display: "detailed",
        value: "",
    },
    topics: {
        label: "Talk to me about...",
        type: "multiselect",
        display: "detailed",
        value: "",
        options: { topicsOptions }
    },
    interests: {
        label: "Interests and hobbies",
        type: "multiselect",
        display: "detailed",
        value: "",
        options: { interestOptions }
    }
};

function formReducer(prevState, { value, key }) {
    console.log("reducer is being updated with this value: ", value);
    const updatedElement = { ...prevState[key] };
    switch (prevState[key].type) {
        case "boolean":
            updatedElement.value = !prevState[key].value; //toggle if boolean
            return { ...prevState, [key]: updatedElement };
        case "string":
            updatedElement.value = value;
            return { ...prevState, [key]: updatedElement };
        case "long string":
            updatedElement.value = value;
            return { ...prevState, [key]: updatedElement };
        case "multiselect":
            updatedElement.value = value;
            return { ...prevState, [key]: updatedElement };
        default:
            return prevState;
    }
}

const InputMultiselect = ({ id, changed, field, value, label, options }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, width: '100%' }}>
        <Label sx={{ fontWeight: "bold", width: '250px', textAlign: 'right', mr: 2 }}> {label} </Label>
        <CreatableMulti
            value={value}
            field={field}
            changed={changed}
            key={field}
            options={options}
            sx={{ width: "100%" }}
        />
    </Box>
);

const Profile = () => {
    const { 
        profile, 
        loading, 
        error, 
        updateProfileField, 
        updateProfileBatch,
        uploadPhotoToFirebaseStorage,
        companyOptions,
        interestOptions,
        addCompany,
        addInterest,
        fetchCompanies,
        fetchInterests,
        seasCourseOptions
    } = useFirebaseProfile();

    const [localProfile, setLocalProfile] = useState({});
    const [uploading, setUploading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '' });
    //const [toastMessage, setToastMessage] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (profile) {
            console.log("Profile data received:", profile);
            setLocalProfile(profile);
        }
    }, [profile]);

    const handleMultiselect = (value, key) => {
        setLocalProfile(prev => ({ ...prev, [key]: value }));
    
        // Handling companies
        if (key === 'companies') {
            handleCompanyChange(value)
            const newOptions = value.filter(option => 
                !companyOptions.some(existingOption => existingOption.value === option.value)
            );
            if (newOptions.length > 0) {
                newOptions.forEach(addCompany); // Adding new company to the options list
            }
        }
    
        // Handling interests
        if (key === 'interests') {
            handleInterestChange(value)
            const newOptions = value.filter(option => 
                !interestOptions.some(existingOption => existingOption.value === option.value)
            );
            if (newOptions.length > 0) {
                newOptions.forEach(addInterest); // Adding new interest to the options list
            }
        }
    };
    
    

    const handleCompanyChange = async (newValue) => {
        const companies = newValue.map(item => item.value);
        setLocalProfile(prev => ({ ...prev, companies }));

        for (let company of companies) {
            if (!companyOptions.some(option => option.value === company)) {
                await addCompany(company);
            }
        }
    };

    const linkAlumniAccount = async () => {
        const user = auth.currentUser;
        if (!user) {
            console.error("no user is currently signed in.");
            return;
        }
    
        const provider = new GoogleAuthProvider();
    
        try {
            const result = await linkWithPopup(user, provider);
            console.log("alumni account linked!", result.user);
            alert("alumni account successfully linked!");
        } catch (error) {
            if (error.code === 'auth/credential-already-in-use') {
                alert("this google account is already linked to another user.");
            } else {
                console.error("error linking accounts:", error);
                alert("something went wrong — check the console for more info.");
            }
        }
    };    


    const handleInterestChange = async (newValue) => {
        const interests = newValue.map(item => item.value);
        setLocalProfile(prev => ({ ...prev, interests }));
        for (let interest of interests) {
            if (!interestOptions.some(option => option.value === interest)) {
                await addInterest(interest);
            }
        }
    };

    const handleCoursesChange = (newValue) => {
        const courses = newValue.map(item => item.value);
        setLocalProfile(prev => ({ ...prev, courses }));
    };

    const handleCoursesTaughtChange = (newValue) => {
        const coursesTaught = newValue.map(item => item.value);
        setLocalProfile(prev => ({ ...prev, coursesTaught }));
    };

    const handleInputChange = (field, value) => {
        setLocalProfile(prev => ({ ...prev, [field]: value }));
    };    

    const showToast = (message) => {
        console.log("Showing toast with message:", message);
        setToast({ show: true, message });
        setTimeout(() => {
            console.log("Hiding toast");
            setToast({ show: false, message: '' });
        }, 3000);
    };

    const handleUpdateOverall = async () => {
        const hasChanged = Object.keys(localProfile).some(
            key => localProfile[key] !== profile[key]
        );
    
        if (!hasChanged) {
            setToast({ show: true, message: 'No changes made to profile.' });
            return;
        }
    
        try {
            await updateProfileBatch({
                displayName: localProfile.displayName,
                occupation: localProfile.occupation,
                classYear: localProfile.classYear,
                concentration: localProfile.concentration,
                email: localProfile.email,
                linkedin: localProfile.linkedin,
                instagram: localProfile.instagram,
                bio: localProfile.bio,
                companies: localProfile.companies,
                courses: localProfile.courses,
                coursesTaught: localProfile.coursesTaught,
                interests: localProfile.interests
            });
    
            setToast({ show: true, message: 'Profile updated successfully!' });
        } catch (error) {
            console.error('Error updating profile:', error);
            setToast({ show: true, message: 'Error updating profile. Please try again.' });
        }

    };

    const handlePhotoUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            setUploading(true);
            try {
                const photoURL = await uploadPhotoToFirebaseStorage(file);
                setLocalProfile(prev => ({ ...prev, photoURL }));
                await updateProfileField('photoURL', photoURL);
                showToast('Profile photo updated successfully!');
            } catch (error) {
                console.error('Error uploading photo:', error);
                showToast('Error uploading photo. Please try again.');
            } finally {
                setUploading(false);
            }
        }
    };

    const isDefaultPhoto = localProfile.photoURL && localProfile.photoURL.startsWith("https://lh3.googleusercontent.com/");

    return (
        <div style={{ width: '100%', display: 'flex' }}>
            <Box sx={{ width: '100%', p: 4, fontFamily: 'body', margin: 3 }}>
                {!loading ? (
                <Flex sx={{ flexDirection: ['column', null, 'row'], flexWrap: 'wrap',justifyContent: 'space-between' }}>
                <Box sx={{ width: ['90%', null, '28%'], pr: [0, null, 4], mb: [4, null, 0] }}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4, }}>
                        <Box 
                            sx={{ 
                                position: 'relative',
                                width: ['60%', '70%'],
                                paddingBottom: ['60%', '70%'],
                                borderRadius: '50%',
                                overflow: 'hidden',
                                '&:hover': {
                                    '& > button': {
                                        transform: 'translateY(0)',
                                        opacity: 1,
                                    },
                                },
                            }}
                        >
                            <Image
                                src={isDefaultPhoto ? defaultPicture : localProfile.photoURL}
                                alt="Profile Picture"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                            <Button 
                                onClick={() => fileInputRef.current.click()}
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '40%',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    padding: 2,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    transform: 'translateY(100%)',
                                    opacity: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    },
                                }}
                            >
                                <Camera size={24} sx={{ mb: 2 }} />
                                <Text sx={{ fontSize: 1 }}>Change Photo</Text>
                            </Button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handlePhotoUpload}
                                style={{ display: 'none' }}
                                accept="image/*"
                            />
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <ProfileInput
                            label="NAME"
                            field="displayName"
                            placeholder="Enter your name"
                            value={localProfile.displayName || ''}
                            onChange={handleInputChange}
                        />
                        <ProfileInput
                            label="OCCUPATION"
                            field="occupation"
                            placeholder="Enter your occupation"
                            value={localProfile.occupation || ''}
                            onChange={handleInputChange}
                        />
                        <ProfileInput
                            label="CLASS YEAR"
                            field="classYear"
                            placeholder="Choose your class year"
                            value={localProfile.classYear || ''}
                            onChange={handleInputChange}
                            options={Array.from({ length: 91 }, (_, i) => 2000 + i)} // Generates years 2000-2090
                        />
                        <ProfileInput
                            label="CONCENTRATION"
                            field="concentration"
                            placeholder="Enter your concentration(s)"
                            value={localProfile.concentration || ''}
                            onChange={handleInputChange}
                            options={concentrationsList}
                            multiple
                        />

                        <Box sx={{ mb: 2, }}>
                            <Text sx={{ fontSize: '14px', lineHeight: '18px', letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 'bold', mb: 2 }}>Contact</Text>
                            <ProfileInput
                                placeholder="Email"
                                field="email"
                                value={localProfile.email || ''}
                                onChange={handleInputChange}
                            />
                            <ProfileInput
                                placeholder="LinkedIn"
                                field="linkedin"
                                value={localProfile.linkedin || ''}
                                onChange={handleInputChange}
                            />
                            <ProfileInput
                                placeholder="Instagram"
                                field="instagram"
                                value={localProfile.instagram || ''}   
                                onChange={(field, val) => handleInputChange(field, val.replace(/^@+/, ''))}
                            />

                            <Button
                                onClick={linkAlumniAccount}
                                sx={{ 
                                    display: 'block',
                                    margin: 'auto',
                                    mt: 4,
                                    bg: 'primary',
                                    color: 'background',
                                    backgroundColor: '#6E4884',
                                    px: 3,
                                    py: 2,
                                    borderRadius: '13px',
                                    '&:hover': {
                                        backgroundColor: '#72617B'
                                    }
                                }}
                            >
                                Link Alumni Account
                            </Button>

                            <Box sx={{ textAlign: 'center',  mt:1,}}>
                                <Text sx = {{ fontSize: 1, fontWeight: 'normal', mb: 3, color: 'grey', fontStyle: 'italic', }}>for graduating seniors only!</Text>
                            </Box>

                        </Box>             
                    </Box>

                    </Box>

                    <Box sx={{ width: ['100%', null, '68%'], pr: [0, null, 4], mb: [4, null, 0], display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: 4 }}>
                            <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>Bio</Text>
                            <InputContainer>
                            <StyledTextarea
                                value={localProfile.bio || ''}
                                onChange={(e) => handleInputChange('bio', e.target.value)}
                                sx={{
                                    width: '100%',
                                    minHeight: 100,
                                    p: 2,
                                    borderColor: 'gray',
                                    borderRadius: 4
                                }}
                            />
                            </InputContainer>
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Text sx={{ fontSize: 3, fontWeight: 'bold', mb: 2 }}>Companies</Text>

                                <CreatableMulti
                                    value={localProfile.companies ? localProfile.companies.map(company => ({ value: company, label: company })) : []}
                                    // onChange={handleCompanyChange}
                                    onChange={(newValue) => handleMultiselect(newValue, 'companies')}
                                    options={companyOptions}
                                    placeholder="+ Add a company"
                                    styles={customStyles}
                                    isMulti
                                />

                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Text sx={{ fontSize: 3, fontWeight: 'bold', mb: 3 }}>Courses I've Taken</Text>
                                <CreatableMulti
                                    value={localProfile.courses ? localProfile.courses.map(course => ({ value: course, label: course })) : []}
                                    onChange={handleCoursesChange}
                                    options={seasCourseOptions}
                                    placeholder="Select SEAS courses"
                                    styles={customStyles}
                                    isMulti
                                    isCreatable={false}
                                />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Text sx={{ fontSize: 3, fontWeight: 'bold', mb: 3 }}>Courses I've Taught</Text>
                                <CreatableMulti
                                    value={localProfile.coursesTaught ? localProfile.coursesTaught.map(course => ({ value: course, label: course })) : []}
                                    onChange={handleCoursesTaughtChange}
                                    options={seasCourseOptions}
                                    placeholder="Select SEAS courses"
                                    styles={customStyles}
                                    isMulti
                                    isCreatable={false}
                                />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Text sx={{ fontSize: 3, fontWeight: 'bold', mb: 3, }}>Interests</Text> 
                                <CreatableMulti
                                    value={localProfile.interests ? localProfile.interests.map(interest => ({ value: interest, label: interest })) : []}
                                    onChange={(newValue) => handleMultiselect(newValue, 'interests')}
                                    options={interestOptions}
                                    placeholder="Select Interests"
                                    styles={customStyles}
                                    isMulti
                                    // isCreatable={true}
                                />
                            <Text sx = {{ fontSize: 2, fontWeight: 'normal', mb: 3, color: 'grey', fontStyle: 'italic', }}> Suggestions: coding, reading, karaoke, art history, animals, volunteering</Text>
                        </Box>

                        {/* Add more sections for SEAS Courses, Extracurriculars, etc. */}
                        <Button 
                            onClick={handleUpdateOverall}
                            sx={{ 
                                display: 'block',
                                margin: 'auto',
                                mt: 4,
                                bg: 'primary',
                                color: 'background',
                                backgroundColor: '#6E4884',
                                px: 3,
                                py: 2,
                                borderRadius: '13px',
                                '&:hover': {
                                    backgroundColor: '#72617B'
                                }
                            }}
                        >
                            Save Profile Changes
                        </Button>
                    </Box>
                </Flex>
                ) : (
                    <Text sx={{ color: 'black', fontSize: 3 }}>Welcome to WiCS! Loading your Profile...</Text>
                )}
                {toast.show && (
                    <Toast
                        message={toast.message}
                        onClose={() => {
                            console.log("Toast closed by user");
                            if (toast.timeoutId) {
                                clearTimeout(toast.timeoutId);
                            }
                            setToast({ show: false, message: '', timeoutId: null });
                        }}
                    />
                )}
            </Box>
        </div>
    );
};

export default Profile;